import {
	Accordion,
	AccordionItem,
	Avatar,
	Spinner,
	Textarea,
	addToast,
} from "@heroui/react";
import { cn } from "@heroui/theme";
import { Icon } from "@iconify/react";
import { useLanguage, useUserInfos } from "@onesolution/hooks";
import type { OperationMessage } from "@onesolution/monday/queries/operationDetails.graphql";
import { addMessageOnServer } from "@onesolution/serverFn/operations/addMessage";
import parseMessageForNotif from "@onesolution/utils/parseMessageForNotif";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import defaultAvatar from "../../assets/altavia-avatar.png";

export default function Messenger({
	messages,
	operationId,
}: { messages: OperationMessage[]; operationId: string }) {
	const userInfos = useUserInfos();
	const { t, locale } = useLanguage();
	const [message, setMessage] = useState("");
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const addMessageMutation = addMessageOnServer.useMutation();
	const queryClient = useQueryClient();
	const isFetching = useIsFetching({ queryKey: ["operationDetails"] });

	const addMessage = useCallback(async () => {
		await addMessageMutation.mutate({
			message: `[${userInfos.email}] [${userInfos.name}]
${message}`,
			operationId,
		});
		queryClient.invalidateQueries({
			queryKey: ["operationDetails"],
		});
		addToast({
			title: t("basic.success"),
			description: t("message.toast-message-success"),
			color: "success",
		});
		setMessage("");
	}, [
		queryClient.invalidateQueries,
		addMessageMutation,
		t,
		operationId,
		message,
		userInfos.email,
		userInfos.name,
	]);

	return (
		<div className="flex overflow-visible w-full">
			<div
				className="overflow-y-auto w-full data-[top-scroll=true]:[mask-image:linear-gradient(0deg,#000_calc(100%_-_var(--scroll-shadow-size)),transparent)] data-[bottom-scroll=true]:[mask-image:linear-gradient(180deg,#000_calc(100%_-_var(--scroll-shadow-size)),transparent)] data-[top-bottom-scroll=true]:[mask-image:linear-gradient(#000,#000,transparent_0,#000_var(--scroll-shadow-size),#000_calc(100%_-_var(--scroll-shadow-size)),transparent)] flex max-h-[calc(100vh-220px)] flex-col gap-6 px-6 py-4 lg:max-h-[calc(100vh-162px)]"
				data-orientation="vertical"
				data-top-scroll="false"
				data-bottom-scroll="true"
			>
				<Accordion selectedKeys={isFetching ? ["1"] : []}>
					<AccordionItem
						key="1"
						aria-label="loading"
						title=""
						classNames={{
							heading: "h-0 p-0 m-0 bg-red-500",
							indicator: "hidden",
						}}
					>
						<div className="w-full flex justify-center">
							<Spinner />
						</div>
					</AccordionItem>
				</Accordion>
				{!messages.length && !isFetching && (
					<div className="flex flex-col items-center justify-center w-full gap-4">
						<span className="font-bold text-2xl">{t("message.nothing")}</span>
						<span className="text-sm">{t("message.send-message")}</span>
					</div>
				)}
				{messages
					.filter((m) => m.text_body)
					.map((message) => {
						const messageInfos = parseMessageForNotif(message);
						const fromMe = messageInfos?.fromEmail === userInfos.email;
						if (!messageInfos) return null;

						return (
							<div
								className={cn("flex gap-3", fromMe && "flex-row-reverse")}
								key={message.id}
							>
								<div className="relative flex-none">
									<Avatar
										name={messageInfos.fromName[0].toUpperCase()}
										src={
											fromMe
												? userInfos.avatar
												: messageInfos.fromFront
													? undefined
													: defaultAvatar
										}
									/>
								</div>
								<div className="flex w-full flex-col gap-4">
									<div className="rounded-medium bg-content2 text-default-600 relative w-full px-4 py-3">
										<div className="flex gap-8">
											<div className="text-small text-default-foreground w-full font-semibold">
												{messageInfos.fromName}
											</div>
											<div className="flex flex-end text-small text-default-400 w-64 justify-end">
												{new Date(message.created_at).toLocaleDateString(
													locale,
												)}{" "}
												•{" "}
												{new Date(message.created_at).toLocaleTimeString(
													locale,
													{
														hour: "2-digit",
														minute: "2-digit",
														hour12: false,
													},
												)}
											</div>
										</div>
										<div className="text-small text-default-900 mt-2 whitespace-pre-line">
											{messageInfos.txt}
										</div>
									</div>
								</div>
							</div>
						);
					})}

				<Textarea
					ref={inputRef}
					className="sticky bottom-0 w-full bg-background rounded-lg"
					minRows={1}
					maxRows={4}
					value={message}
					onValueChange={setMessage}
					variant="bordered"
					endContent={
						addMessageMutation.isPending ? (
							<Spinner />
						) : (
							<Icon
								onClick={addMessage}
								icon="solar:round-arrow-up-bold"
								className="cursor-pointer hover:shadow-sm transition-all rounded-full text-2xl"
							/>
						)
					}
				/>
			</div>
		</div>
	);
}
